import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext.tsx';
import { WorkingDateProvider } from './contexts/WorkingDateContext.tsx';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './data/queryClient.ts';
import CacheIdentityGuard from './data/CacheIdentityGuard.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { BrandLogoMark, BrandWordmark } from './components/BrandLogo.tsx';
import Login from './components/Login.tsx';
import ForgotPassword from './components/ForgotPassword.tsx';
import ProvidersView from './components/ProvidersView.tsx';
import HospitalsView from './components/HospitalsView.tsx';
import OperatingRoomScheduleForm from './components/OperatingRoomScheduleForm.tsx';
import BoardView from './components/BoardView.tsx';
import ManpowerView from './components/ManpowerView.tsx';
import ShiftsPage from './components/ShiftsPage.tsx';
import SimulateForm from './components/SimulateForm.tsx';
import ExternalMappingsManager from './components/ExternalMappingsManager.tsx';
import UsersView from './components/UsersView.tsx';
import ThemeToggle from './components/ThemeToggle.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import RouteErrorBoundary from './components/RouteErrorBoundary.tsx';
import NotFound from './components/NotFound.tsx';
import './App.css';

function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAdmin, logout } = useAuth();

  const navItems = [
    { path: '/simulate', label: 'Simulate', adminOnly: true },
    { path: '/board', label: 'Board' },
    // Manpower = who is where on the day (the former Anesthesiologists + CRNAs tabs, one radio apart).
    { path: '/manpower', label: 'Manpower' },
    // Shifts = planning the roster (formerly labelled "Manpower").
    { path: '/shifts', label: 'Shifts' },
    { path: '/schedules', label: 'Cases' },
    // Providers = the people and their credentials (formerly "Employee Cards" + the Credentials tab).
    { path: '/providers', label: 'Providers' },
    { path: '/hospitals', label: 'Hospitals & ORs' },
    { path: '/mappings', label: 'Mappings' },
    { path: '/users', label: 'Users', adminOnly: true }
    // Billing is intentionally absent: subscriptions are handled by ORchestrate, not by the
    // customer, so the page and its route are unlinked. BillingView.tsx is kept for when
    // self-serve billing is turned back on.
  ].filter(item => isAdmin || !item.adminOnly);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav className="nav">
      <div className="container nav-container">
        <button
          className="hamburger-menu"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <Link to="/" className="brand-mark" onClick={handleNavClick} aria-label="ORchestrate home">
          <BrandLogoMark size={26} />
          <BrandWordmark />
        </Link>
        <ul className={`nav-list ${isMenuOpen ? 'nav-list-open' : ''}`}>
          {navItems.map(item => (
            <li key={item.path} className="nav-item">
              <Link
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        <ThemeToggle />
        {user && !isAdmin && (
          <span
            title="You have read-only access"
            style={{
              fontSize: '11px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-color, var(--text-muted))',
              borderRadius: '999px',
              padding: '2px 9px',
              whiteSpace: 'nowrap',
            }}
          >
            Read-only
          </span>
        )}
        {user && (
          <button
            onClick={handleLogout}
            className="logout-button"
          >
            Logout
          </button>
        )}
      </div>
    </nav>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return <Navigate to="/board" replace />;
  }
  return <>{children}</>;
}

/**
 * App-wide paywall notice. Renders only when the backend reports this tenant as write-blocked,
 * so it stays invisible with enforcement off, for grandfathered tenants, and whenever the
 * status could not be read (the context fails open — see SubscriptionContext).
 */
function SubscriptionNotice() {
  const { canWrite } = useSubscription();
  if (canWrite) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '10px 14px',
        margin: '12px 0',
        borderRadius: '6px',
        fontSize: '13px',
        border: '1px solid var(--danger-text, #dc2626)',
        background: 'rgba(220, 38, 38, 0.08)',
        color: 'var(--text-primary)',
      }}
    >
      <span aria-hidden="true">🔒</span>
      <span>
        <strong>Read-only.</strong> Your ORchestrate subscription is not active, so schedules and
        allocations can be viewed but not changed. Contact ORchestrate support to restore access.
      </span>
    </div>
  );
}

function App() {
  return (
    // Outermost boundary: catches anything the routed boundary cannot, including
    // a throw from the Router or AuthProvider themselves. It hides the "Back to
    // board" link because routing may be exactly what is broken.
    <ErrorBoundary hideBoardLink>
      <Router>
        <AuthProvider>
          {/* In-memory data cache for every screen (data/queryClient.ts). Cleared on sign-out. */}
          <QueryClientProvider client={queryClient}>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <CacheIdentityGuard>
                    <SubscriptionProvider>
                    {/* One working date shared by Board, Manpower and Shifts. */}
                    <WorkingDateProvider>
                      <Navigation />
                      <div className="container">
                        {/* Paywall notice: app-wide, so it is visible from whichever screen
                            the user is on when a write starts failing. */}
                        <SubscriptionNotice />
                        {/* Inside the nav, so a screen crash leaves the menu usable. */}
                        <RouteErrorBoundary>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/board" element={<BoardView />} />
                            <Route path="/manpower" element={<ManpowerView />} />
                            <Route path="/shifts" element={<ShiftsPage />} />
                            <Route path="/providers" element={<ProvidersView />} />
                            {/* Retired tab URLs, kept working for bookmarks and old links. */}
                            <Route path="/anesthesiologists" element={<Navigate to="/manpower?view=anesthesiologists" replace />} />
                            <Route path="/crnas" element={<Navigate to="/manpower?view=crnas" replace />} />
                            <Route path="/employees" element={<Navigate to="/providers" replace />} />
                            <Route path="/credentials" element={<Navigate to="/providers#credentials" replace />} />
                            <Route path="/hospitals" element={<HospitalsView />} />
                            <Route path="/schedules" element={<OperatingRoomScheduleForm />} />
                            <Route path="/simulate" element={<AdminRoute><SimulateForm /></AdminRoute>} />
                            <Route path="/mappings" element={<ExternalMappingsManager />} />
                            <Route path="/users" element={<AdminRoute><UsersView /></AdminRoute>} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </RouteErrorBoundary>
                      </div>
                    </WorkingDateProvider>
                    </SubscriptionProvider>
                    </CacheIdentityGuard>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          </QueryClientProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

function Home() {
  const { isAdmin } = useAuth();
  const items: { path: string; title: string; desc: string; adminOnly?: boolean }[] = [
    { path: '/board', title: 'Board', desc: 'View daily OR schedules and allocations across every hospital, in one timeline.' },
    { path: '/manpower', title: 'Manpower', desc: 'See where every anesthesiologist and CRNA is on the day, and move them between rooms.' },
    { path: '/shifts', title: 'Shifts', desc: 'Define when CRNAs, students, and physicians are available — and where.' },
    { path: '/schedules', title: 'Cases', desc: 'Create and edit operating-room cases for the day.' },
    { path: '/simulate', title: 'Simulate', desc: 'Generate test shifts and schedules to validate the allocation pipeline.' },
    { path: '/providers', title: 'Providers', desc: 'Manage providers, credentials, and home hospitals.' },
    { path: '/hospitals', title: 'Hospitals & ORs', desc: 'Add facilities and manage their operating rooms (Endo, C/S, MRI…).' },
    { path: '/mappings', title: 'Mappings', desc: 'Configure external system identifiers for integration.' },
  ].filter(item => isAdmin || !item.adminOnly);
  return (
    <div style={{ position: 'relative', padding: '32px 4px 16px' }}>
      <p style={{ fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '14px' }}>
        OR Allocation · Console
      </p>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '54px', lineHeight: 1.08, marginBottom: '16px', maxWidth: '780px' }}>
        The <em style={{ fontStyle: 'italic', color: 'var(--or-blue-mid)' }}>OR</em> finally<br />runs itself.
      </h1>
      <p style={{ fontSize: '16px', color: 'var(--text-muted)', maxWidth: '620px', lineHeight: 1.65, marginBottom: '40px', fontWeight: 300 }}>
        Intelligent, one-click scheduling for anesthesia groups. Pick a workspace below to begin.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
        {items.map(item => (
          <Link
            key={item.path}
            to={item.path}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              className="card"
              style={{
                margin: 0,
                padding: '22px 22px 24px',
                borderTop: '2px solid rgba(217, 119, 6, 0.4)',
                height: '100%',
                transition: 'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderTopColor = 'var(--amber)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderTopColor = 'rgba(217, 119, 6, 0.4)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '9px' }}>{item.title}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default App;
